import { Box, DialogContent, DialogTitle, Stack, Tooltip } from '@mui/material'
import { CarrierVisitStatus } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { useTranslate } from '@tolgee/react'
import { InfoIcon, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, ReactNode } from 'react'
import { ICarrierVisitPlanning } from './CarrierVisitPlanning.model'
import { CarrierVisitPlanningCardTitle } from './molecules/CarrierVisitPlanningCardTitle'

interface Props {
  visit: ICarrierVisitPlanning
  descriptionComponent?: ReactNode
  isViewMode?: boolean
  onEdit?: (id: number) => void
}

const ConflictsTooltip: FC<{ visit: ICarrierVisitPlanning }> = ({ visit }) => {
  const { t } = useTranslate()
  const conflicts = []

  if (visit.overlaps) {
    conflicts.push(t('visitOverlapsAnother', 'Visit overlaps another'))
  }

  if (visit.lengthConflict) {
    conflicts.push(
      t(
        'railcarLengthHigherThanTrackLength',
        'Total length of railcars allocated to track exceeds the length of the rail track',
      ),
    )
  }

  if (visit.railcarLengthMissing) {
    conflicts.push(
      t(
        'railcarLengthMissing',
        'Unable to calculate the train length due to railcars with missing lengths',
      ),
    )
  }

  return (
    <Tooltip
      title={
        <ul>
          {conflicts.map(conflict => (
            <li key={conflict}>{conflict}</li>
          ))}
        </ul>
      }
    >
      <InfoIcon color='error' />
    </Tooltip>
  )
}

export const isCarrierVisitEditable = (status: CarrierVisitStatus) => {
  const nonEditableStatuses: CarrierVisitStatus[] = ['Departed', 'Cancelled', 'Closed']
  return !nonEditableStatuses.includes(status)
}

export const CarrierVisitPlanningCard = observer(
  ({ visit, descriptionComponent, onEdit, isViewMode }: Props) => {
    const theme = useMinimalsTheme()
    const { dialogStore } = usePlanningStore()

    const getBorder = () => {
      if (visit.selected) return `4px solid ${theme.palette.primary.main}`

      if (visit.overlaps) return `2px solid ${theme.palette.error.main}`

      return undefined
    }

    const getBackground = () => {
      if (visit.overlaps)
        return `linear-gradient(135deg, ${theme.palette.error.main} 12.5%, ${theme.palette.common.white} 12.5%, 
                ${theme.palette.common.white} 55%, ${theme.palette.error.main} 55%, ${theme.palette.error.main} 60%, 
                ${theme.palette.common.white} 60%, ${theme.palette.common.white} 100%)`

      return theme.palette.common.white
    }

    const visitConflicted = visit.overlaps || visit.lengthConflict || visit.railcarLengthMissing

    const handleCardClick = () => {
      if (!isViewMode) return

      dialogStore.openDialog(
        <>
          <DialogTitle>
            <CarrierVisitPlanningCardTitle
              visit={visit}
              onEdit={isCarrierVisitEditable(visit.status) ? onEdit : undefined}
              onClose={() => dialogStore.close()}
            />
          </DialogTitle>
          <DialogContent sx={{ padding: '18px' }}>{descriptionComponent}</DialogContent>
        </>,
        false,
        {
          open: true,
          slotProps: {
            paper: {
              sx: {},
            },
          },
        },
      )
    }

    return (
      <Box
        sx={{
          position: 'absolute',
          top: `${visit.y}px`,
          left: `${visit.x}px`,
          height: `${visit.height}px`,
          width: `${visit.width}px`,
          background: getBackground(),
          backgroundSize: '6px 6px',
          overflow: 'hidden',
          borderRadius: theme.customRadius.medium,
          boxSizing: 'border-box',
          padding: theme.customSpacing.m,
          border: getBorder(),
          boxShadow: '-1px 1px 5px 4px rgba(223,227,232,1)',
          display: 'flex',
          alignItems: 'center',
          zIndex: 100,
          cursor: isViewMode ? 'pointer' : undefined,
        }}
        onClick={() => handleCardClick()}
      >
        <Box overflow='hidden' width={'100%'}>
          {visitConflicted && (
            <Stack alignItems='center'>
              <ConflictsTooltip visit={visit} />
            </Stack>
          )}

          <CarrierVisitPlanningCardTitle
            visit={visit}
            onEdit={isCarrierVisitEditable(visit.status) ? onEdit : undefined}
            showCarrierVisitAdditionalInfo={visit.width > 400}
          />

          {visit.height > 100 && visit.width <= 400 && (
            <CarrierVisitPlanningCardTitle
              visit={visit}
              onEdit={isCarrierVisitEditable(visit.status) ? onEdit : undefined}
              showCarrierVisitName={false}
              showCargoType={visit.width > 200}
            />
          )}
          {visit.height > 100 && descriptionComponent}
        </Box>
      </Box>
    )
  },
)
